import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'ul',
  'ol',
  'li',
  'a',
  'img',
  'h2',
  'h3',
  'blockquote',
  'code',
  'pre',
  'hr',
] as const;

/** Keep in sync with platform-server productDescriptionHtml.util.ts */
const ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'src',
  'alt',
  'width',
  'height',
  'referrerpolicy',
];

/**
 * Parse HTML without attaching to the live document so <img> does not prefetch
 * before referrerpolicy is set.
 */
function parseHtmlFragment(html: string): HTMLElement | null {
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body;
}

/** Safe HTML for embedding in the product page (matches server sanitize rules). */
export function sanitizeProductDescriptionForDisplay(html: string): string {
  const safe = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });

  // Force no-referrer on images so legacy descriptions (saved before the
  // server transform) do not leak the product page URL to third-party hosts.
  if (!safe.includes('<img')) {
    return safe;
  }

  const body = parseHtmlFragment(safe);
  if (!body) return safe;

  body.querySelectorAll('img').forEach((img) => {
    img.setAttribute('referrerpolicy', 'no-referrer');
  });
  return body.innerHTML;
}

/** True if sanitized HTML has visible text (not only empty paragraphs). */
export function hasVisibleProductDescription(html: string): boolean {
  const safe = sanitizeProductDescriptionForDisplay(html);
  const body = parseHtmlFragment(safe);
  if (!body) {
    // SSR / no DOMParser: strip tags roughly
    return safe.replace(/<[^>]*>/g, '').trim().length > 0;
  }
  return (body.textContent || '').trim().length > 0;
}
